import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import axios from 'axios';
import Cookies from 'js-cookie';
import { triggerAlert } from '../../utils/commonFunctions';
import { ConfirmationAlert } from '../../utils';
import Header from '../../common/Header';
import Sidebar from '../../common/Sidebar';
import PageHeader from '../PageHeader';
import Loader from '../../common/Loader';

export default function AIWorkflowEdit() {
    document.title = "Digital Employees | AI Workflow Edit";
    const mainHeading = "Edit AI Workflow";
    const heading = `Configurations / AI Workflows / Edit`;

    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const filename = params.get('filename');

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors }
    } = useForm({
        defaultValues: {
            name: '',
            description: '',
            pack_price: '',
            pack_units: '',
            unit_label: '',
            features: [{ value: '' }],
            disclaimer: '',
            marketplace_image_url: '',
            // Workflow file-storage entitlements (admin-controlled, live-read
            // on the customer side at quota-enforcement time).
            workflow_storage_mb: 100,
            workflow_max_file_mb: 50,
            workflow_log_retention_days: 30,
        }
    });

    const marketplaceImageUrl = watch('marketplace_image_url') || '';
    const [imageUploading, setImageUploading] = useState(false);

    const handleImageUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (e.target) e.target.value = '';
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            triggerAlert('error', 'Too large', 'Max upload size is 2 MB.');
            return;
        }
        setImageUploading(true);
        try {
            const token = Cookies.get('admin_user_token');
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(
                `/api/admin/ai-workflows/marketplace-image/?filename=${encodeURIComponent(filename)}`,
                fd,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setValue('marketplace_image_url', res.data?.marketplace_image_url || '', { shouldDirty: false });
            triggerAlert('success', '', 'Thumbnail uploaded.');
        } catch (err) {
            const msg = err?.response?.data?.error || 'Upload failed.';
            triggerAlert('error', '', msg);
        } finally {
            setImageUploading(false);
        }
    };

    const handleImageClear = async () => {
        setImageUploading(true);
        try {
            const token = Cookies.get('admin_user_token');
            await axios.delete(
                `/api/admin/ai-workflows/marketplace-image/?filename=${encodeURIComponent(filename)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setValue('marketplace_image_url', '', { shouldDirty: false });
            triggerAlert('success', '', 'Thumbnail cleared.');
        } catch (err) {
            triggerAlert('error', '', 'Could not clear thumbnail.');
        } finally {
            setImageUploading(false);
        }
    };

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'features'
    });

    useEffect(() => {
        if (filename) {
            fetchWorkflowDetails(filename);
        }
    }, [filename]);

    const fetchWorkflowDetails = async (filename) => {
        setIsLoading(true);
        try {
            const token = Cookies.get("admin_user_token");
            const response = await axios.get(`/api/admin/ai-workflows/?filename=${filename}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200) {
                const data = response.data.data || response.data;
                const featuresArr = Array.isArray(data.features) && data.features.length > 0
                    ? data.features.map((f) => ({ value: typeof f === 'string' ? f : (f.value || '') }))
                    : [{ value: '' }];

                reset({
                    name: data.name || '',
                    description: data.description || '',
                    pack_price: data.pack_price !== undefined && data.pack_price !== null ? data.pack_price : '',
                    pack_units: data.pack_units !== undefined && data.pack_units !== null ? data.pack_units : '',
                    unit_label: data.unit_label || '',
                    features: featuresArr,
                    disclaimer: data.disclaimer || '',
                    marketplace_image_url: data.marketplace_image_url || '',
                    workflow_storage_mb: data.workflow_storage_mb ?? 100,
                    workflow_max_file_mb: data.workflow_max_file_mb ?? 50,
                    workflow_log_retention_days: data.workflow_log_retention_days ?? 30,
                });
            }
        } catch (error) {
            triggerAlert('error', '', error.response?.data?.message || 'Failed to fetch workflow details');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (formData) => {
        setIsSubmitting(true);
        try {
            const token = Cookies.get("admin_user_token");

            const payload = {
                name: formData.name,
                description: formData.description,
                pack_price: formData.pack_price !== '' && formData.pack_price !== null && formData.pack_price !== undefined ? parseFloat(formData.pack_price) : 0,
                pack_units: formData.pack_units !== '' && formData.pack_units !== null && formData.pack_units !== undefined ? parseFloat(formData.pack_units) : null,
                unit_label: formData.unit_label || '',
                features: (formData.features || [])
                    .map((f) => (f.value || '').trim())
                    .filter((v) => v !== ''),
                disclaimer: formData.disclaimer,
                // Storage entitlements — coerce blanks to plan defaults so a
                // hand-cleared field doesn't NaN the column.
                workflow_storage_mb: parseInt(formData.workflow_storage_mb, 10) || 100,
                workflow_max_file_mb: parseInt(formData.workflow_max_file_mb, 10) || 50,
                workflow_log_retention_days: parseInt(formData.workflow_log_retention_days, 10) || 30,
            };

            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            const response = await axios.put(`/api/admin/ai-workflows/?filename=${filename}`, payload, config);

            if (response.status === 200 || response.status === 201) {
                triggerAlert('success', '', 'AI Workflow updated successfully!');
                setTimeout(() => {
                    navigate('/configurations/aiworkflows');
                }, 400);
            }
        } catch (error) {
            triggerAlert('error', '', error.response?.data?.message || 'Failed to save AI workflow');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div id="layout-wrapper">
            <Header />
            <Sidebar />
            <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                <div className="main-content">
                    <PageHeader mainHeading={mainHeading} heading={heading} />
                    <div className="container-fluid">

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <button
                                type="button"
                                className="btn btn-light d-flex align-items-center gap-1"
                                onClick={() => navigate('/configurations/aiworkflows')}
                            >
                                <i className="bi bi-arrow-left"></i> Back to list
                            </button>
                        </div>

                        {isLoading ? (
                            <div className='loader-overlay text-white'>
                                <Loader />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="card border-0 shadow-sm mb-3">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-bold" style={{ color: '#014670' }}>
                                            <i className="bi bi-gear-fill me-2"></i>
                                            Workflow Details
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                        placeholder="e.g., Customer Onboarding Flow"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('name', { required: 'Name is required' })}
                                                    />
                                                    {errors.name && (
                                                        <div className="invalid-feedback">{errors.name.message}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Agent Price ($) <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className={`form-control ${errors.pack_price ? 'is-invalid' : ''}`}
                                                        placeholder="e.g., 9.99"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('pack_price', {
                                                            required: 'Agent Price is required',
                                                            min: { value: 0, message: 'Agent Price must be 0 or greater' }
                                                        })}
                                                    />
                                                    {errors.pack_price && (
                                                        <div className="invalid-feedback">{errors.pack_price.message}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Duration ({watch('unit_label') || 'mins'}) <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className={`form-control ${errors.pack_units ? 'is-invalid' : ''}`}
                                                        placeholder="e.g., 100"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('pack_units', {
                                                            required: 'Duration is required',
                                                            min: { value: 0, message: 'Duration must be 0 or greater' }
                                                        })}
                                                    />
                                                    {errors.pack_units && (
                                                        <div className="invalid-feedback">{errors.pack_units.message}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Unit Label</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="e.g., mins"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('unit_label')}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <hr className="my-1" />
                                                <h6 className="mb-2 text-muted" style={{ fontWeight: 600 }}>
                                                    Workflow file storage
                                                </h6>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Storage allowance (MB)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        min="1"
                                                        max="102400"
                                                        className="form-control"
                                                        placeholder="100"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('workflow_storage_mb', { valueAsNumber: true })}
                                                    />
                                                    <small className="text-muted">Total S3 footprint per workflow. Active files (in current saved_inputs) are never evicted.</small>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Max file size (MB)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        min="1"
                                                        max="10240"
                                                        className="form-control"
                                                        placeholder="50"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('workflow_max_file_mb', { valueAsNumber: true })}
                                                    />
                                                    <small className="text-muted">Per-file cap. Uploads above this are rejected at the field level (413).</small>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">
                                                        Log retention (days)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        min="1"
                                                        max="365"
                                                        className="form-control"
                                                        placeholder="30"
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('workflow_log_retention_days', { valueAsNumber: true })}
                                                    />
                                                    <small className="text-muted">How long WorkflowLog rows are kept before nightly pruning.</small>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Description</label>
                                                    <textarea
                                                        rows="3"
                                                        className="form-control"
                                                        placeholder="Briefly describe what this workflow does..."
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register('description')}
                                                    />
                                                </div>
                                            </div>

                                            {/* Marketplace thumbnail — admin-uploaded; falls back to the
                                                AccountTree icon on the customer portal when blank. */}
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Marketplace thumbnail</label>
                                                    <div className="text-muted small mb-2">
                                                        Shown on the marketplace card and on each customer's My Agents listing.
                                                        Recommended: square PNG/JPG, ~256 × 256 px. If blank, the customer portal
                                                        renders the default workflow icon.
                                                    </div>
                                                    <div className="d-flex align-items-center gap-3 p-3" style={{ background: '#f8f9fa', borderRadius: 8, border: '1px dashed #ced4da' }}>
                                                        <div className="flex-shrink-0" style={{ width: 72, height: 72, background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                            {marketplaceImageUrl ? (
                                                                <img src={marketplaceImageUrl} alt="Thumbnail" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                            ) : (
                                                                <i className="bi bi-diagram-3" style={{ fontSize: 28, color: '#7c3aed' }} />
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                            <div style={{ color: '#495057' }}>
                                                                {marketplaceImageUrl ? 'Custom thumbnail set.' : "No custom thumbnail — using the default workflow icon."}
                                                            </div>
                                                            {marketplaceImageUrl && (
                                                                <div className="text-truncate small mt-1" style={{ color: '#6c757d' }} title={marketplaceImageUrl}>
                                                                    {marketplaceImageUrl}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="d-flex flex-column gap-1 flex-shrink-0">
                                                            <label className="btn btn-sm btn-primary mb-0" style={{ cursor: imageUploading ? 'wait' : 'pointer', borderRadius: 6 }}>
                                                                <i className="bi bi-upload me-1" />
                                                                {imageUploading ? 'Uploading…' : (marketplaceImageUrl ? 'Replace' : 'Upload')}
                                                                <input
                                                                    type="file"
                                                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                                    hidden
                                                                    disabled={imageUploading}
                                                                    onChange={handleImageUpload}
                                                                />
                                                            </label>
                                                            {marketplaceImageUrl && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={handleImageClear}
                                                                    disabled={imageUploading}
                                                                    style={{ borderRadius: 6 }}
                                                                >
                                                                    Clear
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm mb-3">
                                    <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 fw-bold" style={{ color: '#014670' }}>
                                            <i className="bi bi-list-check me-2"></i>
                                            Features
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                            onClick={() => append({ value: '' })}
                                        >
                                            <i className="bi bi-plus-lg"></i> Add Feature
                                        </button>
                                    </div>
                                    <div className="card-body p-4">
                                        {fields.length === 0 && (
                                            <p className="text-muted mb-0">No features added yet. Click "Add Feature" to start.</p>
                                        )}

                                        {fields.map((field, index) => (
                                            <div key={field.id} className="d-flex align-items-start gap-2 mb-2">
                                                <span
                                                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary fw-semibold flex-shrink-0"
                                                    style={{ width: 32, height: 32, fontSize: '13px', marginTop: '4px' }}
                                                >
                                                    {index + 1}
                                                </span>
                                                <div className="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors?.features?.[index]?.value ? 'is-invalid' : ''}`}
                                                        placeholder={`Feature ${index + 1}`}
                                                        style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                        {...register(`features.${index}.value`, {
                                                            required: 'Feature cannot be empty'
                                                        })}
                                                    />
                                                    {errors?.features?.[index]?.value && (
                                                        <div className="invalid-feedback">
                                                            {errors.features[index].value.message}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                                    style={{ width: 34, height: 34, backgroundColor: '#da3333ff', color: '#fff', marginTop: '2px' }}
                                                    title="Remove feature"
                                                    onClick={() =>
                                                        ConfirmationAlert(
                                                            'This feature will be removed from the list.',
                                                            'Yes, remove it',
                                                            () => remove(index)
                                                        )
                                                    }
                                                    disabled={fields.length === 1}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm mb-3">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-bold" style={{ color: '#014670' }}>
                                            <i className="bi bi-exclamation-circle me-2"></i>
                                            Disclaimer
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="mb-0">
                                            <label className="form-label fw-semibold">Disclaimer Text</label>
                                            <textarea
                                                rows="5"
                                                className="form-control"
                                                placeholder="Enter the disclaimer text that will be shown to users..."
                                                style={{ borderRadius: '8px', padding: '10px 14px', fontSize: '14px' }}
                                                {...register('disclaimer')}
                                            />
                                            <small className="text-muted">This disclaimer will be displayed to end-users of the workflow.</small>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end gap-2 mb-4">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate('/configurations/aiworkflows')}
                                        style={{ padding: '10px 24px', borderRadius: '8px', fontWeight: 500 }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                        disabled={isSubmitting}
                                        style={{ padding: '10px 24px', borderRadius: '8px', fontWeight: 500, border: 'none' }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg"></i>
                                                Update Workflow
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
